import { motion } from "framer-motion";
import { useOutletContext, Link } from "react-router-dom";
import { Check, ChevronRight, ArrowUpRight } from "lucide-react";
import SEO from "../components/SEO";
import SearchWidget from "../components/SearchWidget";
import Reveal from "../components/Reveal";
import FaqAccordion from "../components/FaqAccordion";
import { t } from "../i18n";
import { byId, cities, staticPath, pagePath, siteUrl } from "../data";

const FEATURED = ["rome", "milan", "naples", "barcelona", "madrid", "paris", "lisbon", "london"];

function MaskedLine({ children, delay }) {
  return (
    <span className="block overflow-hidden pb-1">
      <motion.span
        className="block"
        initial={{ y: "115%" }}
        animate={{ y: 0 }}
        transition={{ duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function HomePage() {
  const { lang } = useOutletContext();
  const i = t[lang];
  const featured = FEATURED.map((id) => byId[id]).filter(Boolean);

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        name: "BoingCars",
        url: siteUrl("/"),
        description: "Independent car rental discovery and comparison website.",
      },
      {
        "@type": "WebSite",
        name: "BoingCars",
        url: siteUrl("/"),
        inLanguage: ["en", "it"],
      },
    ],
  };

  return (
    <>
      <SEO
        lang={lang}
        title={lang === "it"
          ? "BoingCars — Confronta le auto a noleggio. Trova l'offerta giusta."
          : "BoingCars — Compare Car Rentals. Find the Right Deal."}
        description={i.hero.sub}
        pathEn="/en"
        pathIt="/it"
        jsonLd={jsonLd}
      />

      {/* HERO — search first */}
      <section className="relative overflow-hidden bg-sand" data-testid="hero-section">
        <div
          className="pointer-events-none absolute -right-40 -top-40 h-[480px] w-[480px] rounded-full bg-petrol/10 blur-3xl"
          aria-hidden="true"
        />
        <div className="container-site relative z-10 grid gap-8 pb-14 pt-10 md:pt-14 lg:grid-cols-2 lg:items-center lg:gap-14 lg:pb-16">
          <div>
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 rounded-full border border-petrol/20 bg-white/80 px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-petrol"
            >
              {i.hero.kicker}
            </motion.p>
            <h1 className="mt-5 font-display text-4xl font-extrabold leading-[1.04] tracking-tighter text-foreground sm:text-5xl">
              <MaskedLine delay={0.1}>
                <span className="text-petrol">{i.hero.line1}</span>
              </MaskedLine>
              <MaskedLine delay={0.22}>{i.hero.line2}</MaskedLine>
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="mt-4 max-w-md text-base text-muted-foreground md:text-lg"
            >
              {i.hero.sub}
            </motion.p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="rounded-3xl bg-white p-4 shadow-2xl ring-1 ring-black/5 sm:p-6"
            data-testid="hero-widget-card"
          >
            <SearchWidget lang={lang} channel="home" />
          </motion.div>
        </div>

        {/* Trust chips directly under the search */}
        <motion.ul
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.55 }}
          className="container-site relative z-10 flex flex-wrap gap-x-6 gap-y-2 pb-10"
          data-testid="trust-chips"
        >
          {i.hero.points.map((p) => (
            <li key={p} className="flex items-center gap-2 text-xs font-semibold text-foreground/70 sm:text-sm">
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-petrol/10">
                <Check className="h-3 w-3 text-petrol" aria-hidden="true" />
              </span>
              {p}
            </li>
          ))}
        </motion.ul>
        <p className="container-site relative z-10 -mt-6 pb-8 text-[11px] leading-relaxed text-muted-foreground">
          {i.common.affiliateNote}
        </p>
      </section>

      {/* COMPACT DESTINATIONS */}
      <section className="container-site py-14 md:py-20" data-testid="destinations-section">
        <Reveal className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-ember">01</p>
            <h2 className="mt-2 font-display text-2xl font-extrabold tracking-tighter sm:text-3xl lg:text-4xl">
              {i.home.destTitle}
            </h2>
          </div>
          <Link
            to={staticPath(lang, "carRental")}
            data-testid="all-destinations-link"
            className="inline-flex items-center gap-1 text-sm font-semibold text-petrol transition-colors duration-200 hover:text-petrol-light"
          >
            {i.home.viewAll} <ChevronRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        </Reveal>
        <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {featured.map((c, idx) => (
            <Reveal key={c.id} delay={idx * 0.04}>
              <Link
                to={pagePath(lang, c)}
                data-testid={`chip-${c.id}`}
                className="group flex items-center justify-between gap-2 rounded-2xl border border-border bg-white px-4 py-3.5 transition-all duration-200 hover:-translate-y-0.5 hover:border-petrol hover:shadow-md"
              >
                <span className="min-w-0">
                  <span className="block truncate font-display text-sm font-bold tracking-tight">{c.name[lang]}</span>
                  <span className="block text-[11px] font-medium text-muted-foreground">
                    {lang === "it" ? "Noleggio auto" : "Car rental"}
                  </span>
                </span>
                <ArrowUpRight className="h-4 w-4 shrink-0 text-ember transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" aria-hidden="true" />
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* WHY — compact */}
      <section className="bg-white py-14 md:py-20" data-testid="why-section">
        <div className="container-site">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-widest text-ember">02</p>
            <h2 className="mt-2 max-w-xl font-display text-2xl font-extrabold tracking-tighter sm:text-3xl lg:text-4xl">
              {i.home.whyTitle}
            </h2>
            <p className="mt-3 max-w-lg text-sm text-muted-foreground md:text-base">{i.home.whySub}</p>
          </Reveal>
          <div className="mt-10 grid gap-x-10 gap-y-8 sm:grid-cols-2 lg:grid-cols-3">
            {i.home.why.map((w, idx) => (
              <Reveal key={w.t} delay={idx * 0.05} className={idx === 4 ? "sm:col-span-2 lg:col-span-1" : ""}>
                <div className="border-t-2 border-foreground pt-4">
                  <span className="font-display text-3xl font-extrabold tracking-tighter text-petrol/20">
                    {String(idx + 1).padStart(2, "0")}
                  </span>
                  <h3 className="mt-2 font-display text-lg font-bold tracking-tight">{w.t}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{w.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS — compact */}
      <section className="bg-petrol py-14 text-white md:py-20" data-testid="how-section">
        <div className="container-site">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-widest text-ember">03</p>
            <h2 className="mt-2 font-display text-2xl font-extrabold tracking-tighter sm:text-3xl lg:text-4xl">
              {i.home.howTitle}
            </h2>
          </Reveal>
          <div className="mt-10 grid gap-8 md:grid-cols-3 md:gap-10">
            {i.home.steps.map((s, idx) => (
              <Reveal key={s.t} delay={idx * 0.08}>
                <div className="border-t border-white/25 pt-4">
                  <span className="font-display text-4xl font-extrabold tracking-tighter text-ember">{idx + 1}</span>
                  <h3 className="mt-3 font-display text-xl font-bold tracking-tight">{s.t}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-white/70">{s.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="container-site py-14 md:py-20" data-testid="faq-section">
        <div className="grid gap-10 lg:grid-cols-5">
          <Reveal className="lg:col-span-2">
            <p className="text-xs font-bold uppercase tracking-widest text-ember">04</p>
            <h2 className="mt-2 font-display text-2xl font-extrabold tracking-tighter sm:text-3xl">
              {i.home.faqTitle}
            </h2>
            <p className="mt-3 max-w-md text-sm leading-relaxed text-muted-foreground">{i.home.faqSub}</p>
            <Link
              to={staticPath(lang, "help")}
              data-testid="help-link"
              className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-petrol transition-colors duration-200 hover:text-petrol-light"
            >
              {i.nav.help} <ChevronRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </Reveal>
          <Reveal className="lg:col-span-3" delay={0.08}>
            <FaqAccordion faqs={i.help.faqs.slice(0, 6)} testIdPrefix="home-faq" />
          </Reveal>
        </div>
      </section>
    </>
  );
}
